import React from 'react';
import { RCSkeleton, RCImage, RCText } from '../';

interface LinkElementProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  className?: string;
  children?: React.ReactNode;
}

interface RCProductCardProps {
  title: string;
  eyebrow: string;
  imageUrl: string;
  details?: string | JSX.Element;
  ctaText?: string;
  violator?: string | JSX.Element;
  /** The price guide element (e.g. ProductPricingGuidance)
   * Example: `pricingGuideElement={<ProductPricingGuidance />}`
   */
  pricingGuideElement?: JSX.Element;

  /** The link element (e.g. CoreLink)
   * Example: `linkElement={(children) => (<CoreLink to="/whatever/url/">{children}</CoreLink>)}`
   */
  linkElement?: (children: React.ReactNode) => React.ReactElement<LinkElementProps>;
}

export function RCProductCard({
  title,
  eyebrow,
  imageUrl,
  details = undefined,
  ctaText = undefined,
  violator = undefined,
  pricingGuideElement = undefined,
  linkElement,
}: RCProductCardProps) {
  function PassedLink({ children, className, ...passthroughProps }) {
    if (!linkElement) {
      return <>{children}</>;
    }
    return React.cloneElement(
      linkElement(children),
      {
        className: className,
        ...passthroughProps,
      },
    );
  }

  return (
    <div
      className="rc-product-card"
      data-rc-image-overlay-trigger
    >
      <PassedLink className="rc-product-card__link">
        <div className="rc-product-card__image-wrapper">
          <RCImage
            src={imageUrl}
            overlay="offwhiteWithHover"
            borderRadius="md"
          />
        </div>
        <div className="rc-product-card__content">
          <div className="rc-product-card__primary">
            <div className="rc-product-card__eyebrow">
              <RCText.Eyebrow maxLines={1}>{eyebrow}</RCText.Eyebrow>
            </div>
            <div
              className="rc-product-card__title"
              title={title}
            >
              {title}
            </div>
          </div>
          <div className="rc-product-card__secondary">
            {violator &&
            <div className="rc-product-card__violator">
              {violator}
            </div>
            }
            {details &&
            <div className="rc-product-card__details">
              {details}
            </div>
            }
            {pricingGuideElement}
            {ctaText &&
            <div className="rc-product-card__cta">
              {ctaText}
            </div>
            }
          </div>
        </div>
      </PassedLink>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rc-product-card">
      <div className="rc-product-card__image-wrapper">
        <RCSkeleton width="100%" height="100%" />
      </div>
      <div className="rc-product-card__content">
        <div className="rc-product-card__primary">
          <div className="rc-product-card__title">
            <RCSkeleton width="75%" />
          </div>
        </div>
        <div className="rc-product-card__secondary">
          <div className="rc-product-card__details">
            <RCSkeleton width="0" />
          </div>
        </div>
      </div>
    </div>
  );
}

RCProductCard.Skeleton = Skeleton;
